import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import * as d3 from 'd3'

const CongestionChart = ({ congestionTrend, currentCongestionRate }) => {
  const nodeRef = useRef(null)

  useEffect(() => {
    createCongestionChart()
  }, [congestionTrend, currentCongestionRate])

  const createCongestionChart = () => {
    const dateArray = currentCongestionRate.current_rate.time.split(/[- :]/)
    const currentDate = new Date(dateArray[0], dateArray[1]-1, dateArray[2], dateArray[3], dateArray[4])
    const margin = {top: 10, right: 5, bottom: 30, left: 5}
    const node = nodeRef.current

    const width = node.parentElement.offsetWidth - margin.left - margin.right
    const height = node.parentElement.offsetHeight - margin.top - margin.bottom
    const svg = d3.select(node)
                  .attr("width", width + margin.left + margin.right)
                  .attr("height", height + margin.top + margin.bottom)
                  .append("g")
                  .attr("transform", "translate(" + margin.left + "," + margin.top + ")")

    // 本日（本曜日）のトレンドデータ
    // currentDate.getDay()は日曜〜土曜までのindexが順に0〜6
    // congestionTrend.trendは月曜〜日曜までのindexが順に0~6
    const todaysIndex = (currentDate.getDay() + 6) % 7
    const todaysTrend = congestionTrend.trend[currentDate.getDay()]
    const yesterdayIndex = (todaysIndex + 6) % 6
    const yesterdayTrend = congestionTrend.trend[yesterdayIndex]
    const tomorrowIndex = (todaysIndex + 1) % 6
    const tomorrowTrend = congestionTrend.trend[tomorrowIndex]

    const trendArray = yesterdayTrend.concat(todaysTrend).concat(tomorrowTrend)
    // currentDate.getHours()を堺に、過去3時間分、未来20時間分を取得
    const targetTrendData = trendArray.slice(currentDate.getHours()+24 - 3, currentDate.getHours()+24 + 21)
    const startHour = (currentDate.getHours()+24 - 3)%24

    const trendDataForChart = targetTrendData.map( (currentValue, index) => ({ hour: `${(startHour + index)%24}`, rate: currentValue }) )
    const x = d3.scaleBand()
              .domain(trendDataForChart.map(d => d.hour))
              .range([margin.left, width - margin.right])
              .padding(0.1)
    const y = d3.scaleLinear()
                .domain([0, 100]).nice()
                .range([height, 0])
    const xAxis = (g) => (
      g.call(d3.axisBottom(x).tickValues(['0', '3', '6', '9', '12', '15', '18', '21']))
    )

    updateTrendChart(trendDataForChart, svg, height, x, y, xAxis, width, margin)
    updateTodaysCongestionGraph(startHour, currentCongestionRate, x, y, svg)
  }

  // 曜日ごとの混雑度を棒グラフで描画
  const updateTrendChart = (trendDataForChart, svg, height, x, y, xAxis, width, margin) => {
    svg.append("g")
      .attr("transform", "translate(0," + height + ")")
      .call(xAxis)
    for (let i of [25, 50, 75, 100]) {
      svg.append("line")
        .attr("x1", margin.left)
        .attr("y1", y(i))
        .attr("x2", width - margin.right)
        .attr("y2", y(i))
        .attr("stroke-width", 1)
        .attr("stroke", "#D0D0D0");
    }

    svg.append("g")
      .attr("class", "d-StationView-chartBar")
    .selectAll("rect").data(trendDataForChart).enter().append("rect")
      .attr("x", d => x(d.hour))
      .attr("y", d => y(d.rate))
      .attr("height", d => height - y(d.rate))
      .attr("width", x.bandwidth());
  }

  // 本日の混雑度を線グラフで描画
  const updateTodaysCongestionGraph = (startHour, currentCongestionRate, x, y, svg) => {
    const lineData = []
    for (let i of [0, 1, 2]) {
      const todaysRate = currentCongestionRate.rate[currentCongestionRate.rate.length - 1 - 3 + i]
      const obj = {hour: `${(startHour+i)%24}`, currentRate: todaysRate}
      lineData.push({hour: `${(startHour+i)%24}`, currentRate: todaysRate.percentage})
    }
    lineData.push({hour: `${(startHour + 3)%24}`, currentRate: currentCongestionRate.current_rate.percentage})

    const trendLine = d3.line()
                            .x( (d) => x(d.hour) + (x.bandwidth() / 2) )
                            .y( (d) => y(d.currentRate) )

    svg.append("path")
      .datum(lineData)
      .attr("class", "d-StationView-chartLine")
      .attr("d", trendLine)
  }

  return <svg ref={nodeRef} />
}

CongestionChart.propTypes = {
  congestionTrend: PropTypes.object.isRequired,
  currentCongestionRate: PropTypes.object.isRequired,
}

export default CongestionChart
